import { useEffect, useMemo, useState } from "react";
import { api, formatApiError, fileUrl } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "@/components/ui/command";
import { Plus, Pencil, Trash2, Receipt, MinusCircle, PlusCircle, Search, ChevronsUpDown, Check, FileDown } from "lucide-react";
import { toast } from "sonner";

const gbp = (n) => `£${(Number(n) || 0).toFixed(2)}`;
const today = () => new Date().toISOString().slice(0, 10);
const cn = (...c) => c.filter(Boolean).join(" ");

// Empty form — kind is set by which tab you're on when you hit "Add".
const emptyForm = (kind) => ({
  id: null,
  kind,
  date: today(),
  description: "",
  amount: "",
  job_id: "",
  client_id: "",
  contact_id: "",
  contact_type: kind === "deduction" ? "subcontractor" : "",
  status: "pending",
});

const STATUS_OPTIONS = [
  { v: "pending",  label: "Pending",  chip: "bg-indigo-100 text-indigo-800 border-indigo-200" },
  { v: "actioned", label: "Actioned", chip: "bg-blue-100 text-blue-800 border-blue-200" },
  { v: "invoiced", label: "Invoiced", chip: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  { v: "paid",     label: "Paid",     chip: "bg-green-100 text-green-800 border-green-200" },
];
const statusStyle = (s) => STATUS_OPTIONS.find((x) => x.v === s)?.chip || STATUS_OPTIONS[0].chip;

export default function JobCharges() {
  const [tab, setTab] = useState("deduction");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [jobs, setJobs] = useState([]);
  const [clients, setClients] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState(null); // null | "pending" | "actioned" | "invoiced" | "paid"
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm("deduction"));

  const load = async () => {
    setLoading(true);
    try {
      const [adj, j, c, con] = await Promise.all([
        api.get("/adjustments"),
        api.get("/jobs"),
        api.get("/clients"),
        api.get("/contacts"),
      ]);
      setRows(adj.data || []);
      setJobs(j.data || []);
      setClients(c.data || []);
      setContacts((con.data || []).filter((x) => x.type === "subcontractor" || x.type === "self_employed"));
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const jobById = useMemo(() => Object.fromEntries(jobs.map((j) => [j.id, j])), [jobs]);
  const clientById = useMemo(() => Object.fromEntries(clients.map((c) => [c.id, c])), [clients]);
  const contactsByType = useMemo(() => ({
    subcontractor: contacts.filter((c) => c.type === "subcontractor"),
    self_employed: contacts.filter((c) => c.type === "self_employed"),
  }), [contacts]);

  // Reset the status filter when the user switches tabs so they don't get an unexpected empty state.
  useEffect(() => { setStatusFilter(null); }, [tab]);

  const rowsForTab = useMemo(() => {
    const q = search.trim().toLowerCase();
    let arr = rows.filter((r) => r.kind === tab);
    if (statusFilter) arr = arr.filter((r) => (r.status || "pending") === statusFilter);
    if (q) {
      arr = arr.filter((r) =>
        (r.description || "").toLowerCase().includes(q) ||
        (r.job_number || "").toLowerCase().includes(q) ||
        (r.client_name || "").toLowerCase().includes(q) ||
        (r.contact_name || "").toLowerCase().includes(q)
      );
    }
    return arr;
  }, [rows, tab, search, statusFilter]);

  const total = useMemo(() => rowsForTab.reduce((a, r) => a + (Number(r.amount) || 0), 0), [rowsForTab]);

  // KPI summary — computed against every row on the current tab (ignoring the status filter itself,
  // so the tiles always show the full picture even when one is active).
  const kpi = useMemo(() => {
    const all = rows.filter((r) => r.kind === tab);
    const bucket = { pending: [], actioned: [], invoiced: [], paid: [] };
    for (const r of all) {
      const s = (r.status || "pending");
      if (bucket[s]) bucket[s].push(r);
    }
    const sum = (arr) => arr.reduce((a, r) => a + (Number(r.amount) || 0), 0);
    return {
      total_count: all.length,
      total_sum: sum(all),
      pending:   { count: bucket.pending.length,   sum: sum(bucket.pending) },
      actioned:  { count: bucket.actioned.length,  sum: sum(bucket.actioned) },
      invoiced:  { count: bucket.invoiced.length,  sum: sum(bucket.invoiced) },
      paid:      { count: bucket.paid.length,      sum: sum(bucket.paid) },
    };
  }, [rows, tab]);

  const [pdfOpen, setPdfOpen] = useState(false);
  const [pdfForm, setPdfForm] = useState({ date_from: "", date_to: "", scope: "all", client_id: "", contact_id: "", contact_type: "subcontractor" });

  const openPdf = () => {
    setPdfForm((f) => ({
      ...f,
      date_from: "", date_to: "",
      scope: "all", client_id: "", contact_id: "",
      contact_type: tab === "deduction" ? "subcontractor" : f.contact_type,
    }));
    setPdfOpen(true);
  };

  const buildPdfUrl = () => {
    const p = new URLSearchParams({ kind: tab });
    if (pdfForm.date_from) p.set("date_from", pdfForm.date_from);
    if (pdfForm.date_to)   p.set("date_to",   pdfForm.date_to);
    if (pdfForm.scope === "one") {
      if (tab === "charge" && pdfForm.client_id) p.set("client_id", pdfForm.client_id);
      if (tab === "deduction" && pdfForm.contact_id) p.set("contact_id", pdfForm.contact_id);
    }
    return fileUrl(`/adjustments/pdf?${p.toString()}`);
  };

  const openAdd = () => { setForm(emptyForm(tab)); setOpen(true); };
  const openEdit = (r) => {
    setForm({
      id: r.id,
      kind: r.kind,
      date: (r.date || "").slice(0, 10),
      description: r.description || "",
      amount: String(r.amount ?? ""),
      job_id: r.job_id || "",
      client_id: r.client_id || "",
      contact_id: r.contact_id || "",
      contact_type: r.contact_type || (r.kind === "deduction" ? "subcontractor" : ""),
      status: r.status || "pending",
    });
    setOpen(true);
  };

  // Inline status change from the table — one-click update without opening the editor.
  const setStatus = async (row, status) => {
    if ((row.status || "pending") === status) return;
    try {
      await api.put(`/adjustments/${row.id}`, {
        kind: row.kind, date: row.date, description: row.description || "",
        amount: Number(row.amount) || 0,
        job_id: row.job_id || null, client_id: row.client_id || null,
        contact_id: row.contact_id || null, contact_type: row.contact_type || null,
        status,
      });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const submit = async () => {
    const amt = Number(form.amount);
    if (!form.date) return toast.error("Pick a date.");
    if (!(amt > 0)) return toast.error("Amount must be greater than 0.");
    if (!form.description.trim()) return toast.error("Add a description.");

    // Auto-derive client from job when possible so ops don't pick it twice.
    let client_id = form.client_id || "";
    if (form.job_id) {
      client_id = jobById[form.job_id]?.client_id || client_id;
    }
    const payload = {
      kind: form.kind,
      date: form.date,
      description: form.description.trim(),
      amount: amt,
      job_id: form.job_id || null,
      client_id: client_id || null,
      contact_id: form.kind === "deduction" ? (form.contact_id || null) : null,
      contact_type: form.kind === "deduction" ? (form.contact_type || null) : null,
      status: form.status || "pending",
    };
    if (form.kind === "deduction" && !payload.contact_id) return toast.error("Pick the subcontractor or self-employed driver.");
    if (form.kind === "charge" && !payload.job_id && !payload.client_id) return toast.error("Pick a job or a client.");

    try {
      if (form.id) await api.put(`/adjustments/${form.id}`, payload);
      else await api.post("/adjustments", payload);
      toast.success(form.id ? "Updated" : "Added");
      setOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete this ${r.kind === "deduction" ? "deduction" : "charge"}?`)) return;
    try {
      await api.delete(`/adjustments/${r.id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="max-w-6xl mx-auto space-y-6" data-testid="job-charges-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">Operations</div>
          <h1 className="mt-1 text-3xl font-semibold tracking-tight text-slate-900">Charges</h1>
          <p className="text-sm text-slate-500 mt-1">
            Ad-hoc extras billed to a client (chargeable) or amounts taken off what we pay a sub / self-employed driver (deductions). Optionally tied to a job.
          </p>
        </div>
        <Button data-testid="charges-add-btn" onClick={openAdd} className="rounded-sm bg-slate-900 hover:bg-slate-800 text-white">
          <Plus className="w-4 h-4 mr-2" /> Add {tab === "deduction" ? "deduction" : "charge"}
        </Button>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <TabsList className="rounded-sm bg-slate-100">
            <TabsTrigger data-testid="charges-tab-deductions" value="deduction" className="rounded-sm">
              <MinusCircle className="w-4 h-4 mr-2" /> Deductions
            </TabsTrigger>
            <TabsTrigger data-testid="charges-tab-chargeable" value="charge" className="rounded-sm">
              <PlusCircle className="w-4 h-4 mr-2" /> Chargeable
            </TabsTrigger>
          </TabsList>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="w-4 h-4 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
              <Input
                data-testid="charges-search"
                placeholder="Search description, job, client, driver…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-8 h-9 w-72 rounded-sm border-slate-300"
              />
            </div>
            <Button
              variant="outline"
              onClick={openPdf}
              data-testid="charges-pdf-btn"
              className="h-9 rounded-sm border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
              title="Download filtered PDF"
            >
              <FileDown className="w-4 h-4 mr-2" /> PDF
            </Button>
            {tab === "deduction" && (
              <div className="text-sm text-slate-600">
                Total: <span className="font-semibold text-slate-900 tabular-nums">{gbp(total)}</span>
              </div>
            )}
          </div>
        </div>

        {/* KPI summary — click a tile to filter the table by that status. Click again to clear. */}
        <div className="mt-4 grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="charges-kpi">
          <KpiTile
            testid="kpi-total"
            label={tab === "deduction" ? "All deductions" : "All chargeables"}
            value={kpi.total_count}
            sub={gbp(kpi.total_sum)}
            tone={tab === "deduction" ? "rose" : "emerald"}
            active={statusFilter === null}
            onClick={() => setStatusFilter(null)}
          />
          <KpiTile
            testid="kpi-pending"
            label="Pending"
            value={kpi.pending.count}
            sub={gbp(kpi.pending.sum)}
            tone="indigo"
            active={statusFilter === "pending"}
            onClick={() => setStatusFilter((s) => s === "pending" ? null : "pending")}
          />
          <KpiTile
            testid="kpi-actioned"
            label="Actioned"
            value={kpi.actioned.count}
            sub={gbp(kpi.actioned.sum)}
            tone="blue"
            active={statusFilter === "actioned"}
            onClick={() => setStatusFilter((s) => s === "actioned" ? null : "actioned")}
          />
          <KpiTile
            testid="kpi-invoiced"
            label="Invoiced"
            value={kpi.invoiced.count}
            sub={gbp(kpi.invoiced.sum)}
            tone="emerald"
            active={statusFilter === "invoiced"}
            onClick={() => setStatusFilter((s) => s === "invoiced" ? null : "invoiced")}
          />
          <KpiTile
            testid="kpi-paid"
            label="Paid"
            value={kpi.paid.count}
            sub={gbp(kpi.paid.sum)}
            tone="green"
            active={statusFilter === "paid"}
            onClick={() => setStatusFilter((s) => s === "paid" ? null : "paid")}
          />
        </div>

        <TabsContent value="deduction" className="mt-4">
          <ChargesTable
            rows={rowsForTab}
            kind="deduction"
            loading={loading}
            onEdit={openEdit}
            onDelete={remove}
            onStatus={setStatus}
          />
        </TabsContent>
        <TabsContent value="charge" className="mt-4">
          <ChargesTable
            rows={rowsForTab}
            kind="charge"
            loading={loading}
            onEdit={openEdit}
            onDelete={remove}
            onStatus={setStatus}
          />
        </TabsContent>
      </Tabs>

      <ChargeEditor
        open={open}
        onOpenChange={setOpen}
        form={form}
        setForm={setForm}
        jobs={jobs}
        jobById={jobById}
        clients={clients}
        clientById={clientById}
        contactsByType={contactsByType}
        onSubmit={submit}
      />

      <Dialog open={pdfOpen} onOpenChange={setPdfOpen}>
        <DialogContent className="rounded-sm max-w-md" data-testid="charges-pdf-dialog">
          <DialogHeader>
            <DialogTitle>Download {tab === "deduction" ? "deductions" : "chargeable"} PDF</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-slate-600">From date</Label>
                <Input data-testid="pdf-from" type="date" value={pdfForm.date_from}
                  onChange={(e) => setPdfForm((f) => ({ ...f, date_from: e.target.value }))}
                  className="mt-1 rounded-sm border-slate-300" />
              </div>
              <div>
                <Label className="text-xs text-slate-600">To date</Label>
                <Input data-testid="pdf-to" type="date" value={pdfForm.date_to}
                  onChange={(e) => setPdfForm((f) => ({ ...f, date_to: e.target.value }))}
                  className="mt-1 rounded-sm border-slate-300" />
              </div>
            </div>
            <div>
              <Label className="text-xs text-slate-600">
                {tab === "deduction" ? "Subcontractor / self-employed" : "Client"}
              </Label>
              <div className="mt-1 flex gap-2">
                <button
                  type="button"
                  data-testid="pdf-scope-all"
                  onClick={() => setPdfForm((f) => ({ ...f, scope: "all", client_id: "", contact_id: "" }))}
                  className={cn(
                    "px-3 h-9 rounded-sm border text-sm font-medium",
                    pdfForm.scope === "all"
                      ? "bg-slate-900 text-white border-slate-900"
                      : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50",
                  )}
                >All</button>
                <button
                  type="button"
                  data-testid="pdf-scope-one"
                  onClick={() => setPdfForm((f) => ({ ...f, scope: "one" }))}
                  className={cn(
                    "px-3 h-9 rounded-sm border text-sm font-medium",
                    pdfForm.scope === "one"
                      ? "bg-slate-900 text-white border-slate-900"
                      : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50",
                  )}
                >Just one</button>
              </div>
            </div>
            {pdfForm.scope === "one" && tab === "charge" && (
              <div>
                <Label className="text-xs text-slate-600">Client</Label>
                <Select value={pdfForm.client_id || undefined}
                        onValueChange={(v) => setPdfForm((f) => ({ ...f, client_id: v }))}>
                  <SelectTrigger data-testid="pdf-client" className="mt-1 rounded-sm border-slate-300 h-9">
                    <SelectValue placeholder="Pick a client…" />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            {pdfForm.scope === "one" && tab === "deduction" && (
              <>
                <div>
                  <Label className="text-xs text-slate-600">Type</Label>
                  <div className="mt-1 flex gap-2">
                    <button
                      type="button"
                      onClick={() => setPdfForm((f) => ({ ...f, contact_type: "subcontractor", contact_id: "" }))}
                      className={cn("px-3 h-9 rounded-sm border text-sm font-medium",
                        pdfForm.contact_type === "subcontractor"
                          ? "bg-slate-900 text-white border-slate-900"
                          : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50")}
                    >Subcontractor</button>
                    <button
                      type="button"
                      onClick={() => setPdfForm((f) => ({ ...f, contact_type: "self_employed", contact_id: "" }))}
                      className={cn("px-3 h-9 rounded-sm border text-sm font-medium",
                        pdfForm.contact_type === "self_employed"
                          ? "bg-slate-900 text-white border-slate-900"
                          : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50")}
                    >Self-employed</button>
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-slate-600">
                    {pdfForm.contact_type === "self_employed" ? "Self-employed driver" : "Subcontractor"}
                  </Label>
                  <Select value={pdfForm.contact_id || undefined}
                          onValueChange={(v) => setPdfForm((f) => ({ ...f, contact_id: v }))}>
                    <SelectTrigger data-testid="pdf-contact" className="mt-1 rounded-sm border-slate-300 h-9">
                      <SelectValue placeholder="Pick…" />
                    </SelectTrigger>
                    <SelectContent>
                      {(contactsByType[pdfForm.contact_type] || []).map((c) => (
                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}
          </div>
          <DialogFooter className="pt-2">
            <Button variant="outline" onClick={() => setPdfOpen(false)} className="rounded-sm">Cancel</Button>
            <a
              href={buildPdfUrl()}
              download
              data-testid="charges-pdf-generate"
              onClick={() => setPdfOpen(false)}
              className="inline-flex items-center px-4 h-10 rounded-sm bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium"
            >
              <FileDown className="w-4 h-4 mr-2" /> Download PDF
            </a>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------- Table ----------
function ChargesTable({ rows, kind, loading, onEdit, onDelete, onStatus }) {
  const isDeduction = kind === "deduction";
  return (
    <Card className="rounded-sm border border-slate-200 shadow-none overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600 uppercase text-[11px] tracking-wider">
            <tr>
              <th className="text-left px-4 py-2.5">Date</th>
              <th className="text-left px-4 py-2.5">Job</th>
              <th className="text-left px-4 py-2.5">{isDeduction ? "Driver / Sub" : "Client"}</th>
              <th className="text-left px-4 py-2.5">Description</th>
              <th className="text-left px-4 py-2.5">Status</th>
              <th className="text-right px-4 py-2.5">Amount</th>
              <th className="text-right px-4 py-2.5 w-28"></th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={7} className="text-center py-10 text-slate-500 text-sm">Loading…</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={7} className="text-center py-16 text-slate-500 text-sm">
                <Receipt className="w-6 h-6 mx-auto mb-2 opacity-40" />
                No {isDeduction ? "deductions" : "chargeables"} yet — click <span className="font-medium text-slate-700">Add</span> to log one.
              </td></tr>
            )}
            {!loading && rows.map((r) => (
              <tr key={r.id} className="border-t border-slate-100" data-testid={`charge-row-${r.id}`}>
                <td className="px-4 py-2.5 text-slate-700 tabular-nums">{(r.date || "").slice(0, 10)}</td>
                <td className="px-4 py-2.5">
                  {r.job_number ? (
                    <span className="inline-flex items-center rounded-sm bg-slate-100 border border-slate-200 text-slate-700 text-[11px] font-medium px-1.5 py-0.5">
                      {r.job_number}
                    </span>
                  ) : (
                    <span className="text-slate-400 text-xs italic">no job link</span>
                  )}
                </td>
                <td className="px-4 py-2.5 text-slate-800">
                  {isDeduction ? (r.contact_name || "—") : (r.client_name || "—")}
                  {isDeduction && r.contact_type && (
                    <span className="ml-2 text-[10px] uppercase tracking-wider text-slate-500">
                      · {r.contact_type === "self_employed" ? "Self-emp" : "Sub"}
                    </span>
                  )}
                </td>
                <td className="px-4 py-2.5 text-slate-700 max-w-md truncate" title={r.description}>{r.description || "—"}</td>
                <td className="px-4 py-2.5">
                  <Select
                    value={r.status || "pending"}
                    onValueChange={(v) => onStatus(r, v)}
                  >
                    <SelectTrigger
                      data-testid={`charge-status-${r.id}`}
                      className={cn(
                        "h-7 min-w-[110px] rounded-sm border text-[11px] font-semibold uppercase tracking-wide",
                        statusStyle(r.status || "pending")
                      )}
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STATUS_OPTIONS.map((s) => (
                        <SelectItem key={s.v} value={s.v}>{s.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </td>
                <td className={cn(
                  "px-4 py-2.5 text-right tabular-nums font-semibold",
                  isDeduction ? "text-rose-700" : "text-emerald-700"
                )}>
                  {isDeduction ? "−" : "+"}{gbp(r.amount)}
                </td>
                <td className="px-4 py-2.5 text-right">
                  <div className="inline-flex items-center gap-1">
                    <a
                      href={fileUrl(`/adjustments/${r.id}/pdf`)}
                      download
                      rel="noopener"
                      data-testid={`charge-pdf-${r.id}`}
                      title="Download this row as PDF"
                      className="inline-flex items-center justify-center h-8 w-8 rounded-sm text-slate-600 hover:bg-slate-100"
                    >
                      <FileDown className="w-4 h-4" />
                    </a>
                    <Button variant="ghost" size="icon" onClick={() => onEdit(r)}
                      data-testid={`charge-edit-${r.id}`}
                      className="h-8 w-8 text-slate-600 hover:text-slate-900 hover:bg-slate-100">
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => onDelete(r)}
                      data-testid={`charge-delete-${r.id}`}
                      className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

// ---------- Add / Edit dialog ----------
function ChargeEditor({ open, onOpenChange, form, setForm, jobs, jobById, clients, clientById, contactsByType, onSubmit }) {
  const isDeduction = form.kind === "deduction";
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const linkedJob = form.job_id ? jobById[form.job_id] : null;
  const linkedClient = linkedJob?.client_id
    ? clientById[linkedJob.client_id]
    : (form.client_id ? clientById[form.client_id] : null);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm max-w-lg" data-testid="charge-editor">
        <DialogHeader>
          <DialogTitle>
            {form.id ? "Edit" : "Add"} {isDeduction ? "deduction" : "charge"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-slate-600">Date</Label>
              <Input
                data-testid="charge-date"
                type="date"
                value={form.date}
                onChange={(e) => set("date", e.target.value)}
                className="mt-1 rounded-sm border-slate-300"
              />
            </div>
            <div>
              <Label className="text-xs text-slate-600">Amount (£)</Label>
              <Input
                data-testid="charge-amount"
                type="number" step="0.01" min="0"
                value={form.amount}
                onChange={(e) => set("amount", e.target.value)}
                className="mt-1 rounded-sm border-slate-300 tabular-nums"
                placeholder="0.00"
              />
            </div>
          </div>

          <div>
            <Label className="text-xs text-slate-600">
              Job <span className="text-slate-400 font-normal">(optional)</span>
            </Label>
            <JobPicker
              value={form.job_id}
              onChange={(id) => set("job_id", id)}
              jobs={jobs}
              clientById={clientById}
            />
            {linkedJob && (
              <p className="text-[11px] text-slate-500 mt-1">
                Job on {(linkedJob.date || "").slice(0, 10)} · Client: <span className="text-slate-700">{clientById[linkedJob.client_id]?.name || "—"}</span>
              </p>
            )}
          </div>

          {isDeduction && (
            <>
              <div>
                <Label className="text-xs text-slate-600">Deducting from</Label>
                <div className="mt-1 flex gap-2">
                  <button
                    type="button"
                    data-testid="charge-contact-type-sub"
                    onClick={() => { set("contact_type", "subcontractor"); set("contact_id", ""); }}
                    className={cn(
                      "px-3 h-9 rounded-sm border text-sm font-medium",
                      form.contact_type === "subcontractor"
                        ? "bg-slate-900 text-white border-slate-900"
                        : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50",
                    )}
                  >Subcontractor</button>
                  <button
                    type="button"
                    data-testid="charge-contact-type-self"
                    onClick={() => { set("contact_type", "self_employed"); set("contact_id", ""); }}
                    className={cn(
                      "px-3 h-9 rounded-sm border text-sm font-medium",
                      form.contact_type === "self_employed"
                        ? "bg-slate-900 text-white border-slate-900"
                        : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50",
                    )}
                  >Self-employed</button>
                </div>
              </div>
              <div>
                <Label className="text-xs text-slate-600">
                  {form.contact_type === "self_employed" ? "Self-employed driver" : "Subcontractor"}
                </Label>
                <Select value={form.contact_id || undefined} onValueChange={(v) => set("contact_id", v)}>
                  <SelectTrigger data-testid="charge-contact" className="mt-1 rounded-sm border-slate-300 h-9">
                    <SelectValue placeholder="Select…" />
                  </SelectTrigger>
                  <SelectContent>
                    {(contactsByType[form.contact_type] || []).map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </>
          )}

          {!isDeduction && !form.job_id && (
            <div>
              <Label className="text-xs text-slate-600">Client</Label>
              <Select value={form.client_id || undefined} onValueChange={(v) => set("client_id", v)}>
                <SelectTrigger data-testid="charge-client" className="mt-1 rounded-sm border-slate-300 h-9">
                  <SelectValue placeholder="Select client…" />
                </SelectTrigger>
                <SelectContent>
                  {clients.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {!linkedClient && (
                <p className="text-[11px] text-slate-500 mt-1">Pick a job above and the client is filled in for you.</p>
              )}
            </div>
          )}

          <div>
            <Label className="text-xs text-slate-600">Description</Label>
            {isDeduction && (
              <Select
                value=""
                onValueChange={(v) => set("description", v)}
              >
                <SelectTrigger
                  data-testid="charge-reason-preset"
                  className="mt-1 mb-2 rounded-sm border-slate-300 h-9 text-slate-600"
                >
                  <SelectValue placeholder="Quick reason…" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Late delivery">Late delivery</SelectItem>
                  <SelectItem value="Late arrival">Late arrival</SelectItem>
                  <SelectItem value="Not delivered on time">Not delivered on time</SelectItem>
                  <SelectItem value="No POD">No POD</SelectItem>
                  <SelectItem value="Missed drop">Missed drop</SelectItem>
                  <SelectItem value="Damaged goods">Damaged goods</SelectItem>
                  <SelectItem value="Failed collection">Failed collection</SelectItem>
                  <SelectItem value="No show">No show</SelectItem>
                  <SelectItem value="Fuel float">Fuel float</SelectItem>
                  <SelectItem value="Uniform / PPE">Uniform / PPE</SelectItem>
                  <SelectItem value="Van damage">Van damage</SelectItem>
                  <SelectItem value="Fine / penalty charge">Fine / penalty charge</SelectItem>
                </SelectContent>
              </Select>
            )}
            {!isDeduction && (
              <Select
                value=""
                onValueChange={(v) => set("description", v)}
              >
                <SelectTrigger
                  data-testid="charge-extra-preset"
                  className="mt-1 mb-2 rounded-sm border-slate-300 h-9 text-slate-600"
                >
                  <SelectValue placeholder="Quick extra…" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Waiting time">Waiting time</SelectItem>
                  <SelectItem value="Congestion charge">Congestion charge</SelectItem>
                  <SelectItem value="ULEZ charge">ULEZ charge</SelectItem>
                  <SelectItem value="Toll charge">Toll charge</SelectItem>
                  <SelectItem value="Parking / permit">Parking / permit</SelectItem>
                  <SelectItem value="Extra drop">Extra drop</SelectItem>
                  <SelectItem value="Return / redelivery">Return / redelivery</SelectItem>
                  <SelectItem value="Out of hours">Out of hours</SelectItem>
                  <SelectItem value="Weekend surcharge">Weekend surcharge</SelectItem>
                  <SelectItem value="Fuel surcharge">Fuel surcharge</SelectItem>
                  <SelectItem value="Handball / loading">Handball / loading</SelectItem>
                  <SelectItem value="Storage fee">Storage fee</SelectItem>
                  <SelectItem value="Admin fee">Admin fee</SelectItem>
                  <SelectItem value="Cancellation fee">Cancellation fee</SelectItem>
                </SelectContent>
              </Select>
            )}
            <Textarea
              data-testid="charge-description"
              value={form.description}
              onChange={(e) => set("description", e.target.value)}
              rows={3}
              className="mt-1 rounded-sm border-slate-300"
              placeholder={isDeduction ? "e.g. Fuel float, damage, missed drop…" : "e.g. Congestion charge, waiting-time surcharge, admin fee…"}
            />
          </div>

          <div>
            <Label className="text-xs text-slate-600">Status</Label>
            <Select value={form.status || "pending"} onValueChange={(v) => set("status", v)}>
              <SelectTrigger data-testid="charge-status-select" className="mt-1 rounded-sm border-slate-300 h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="actioned">Actioned</SelectItem>
                <SelectItem value="invoiced">Invoiced</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter className="pt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm">Cancel</Button>
          <Button onClick={onSubmit} data-testid="charge-save"
                  className={cn("rounded-sm text-white",
                    isDeduction ? "bg-rose-600 hover:bg-rose-700" : "bg-emerald-600 hover:bg-emerald-700")}>
            {form.id ? "Save" : "Add"} {isDeduction ? "deduction" : "charge"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------- KPI tile ----------
function KpiTile({ label, value, sub, tone = "slate", active = false, onClick, testid }) {
  const tones = {
    indigo:  { base: "border-indigo-200 bg-indigo-50",   ring: "ring-indigo-400"  },
    blue:    { base: "border-blue-200 bg-blue-50",       ring: "ring-blue-400"    },
    emerald: { base: "border-emerald-200 bg-emerald-50", ring: "ring-emerald-400" },
    green:   { base: "border-green-200 bg-green-50",     ring: "ring-green-400"   },
    rose:    { base: "border-rose-200 bg-rose-50",       ring: "ring-rose-400"    },
    slate:   { base: "border-slate-200 bg-white",        ring: "ring-slate-400"   },
  };
  const t = tones[tone] || tones.slate;
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      aria-pressed={active}
      className={cn(
        "text-left rounded-sm border p-4 transition cursor-pointer",
        "hover:-translate-y-[1px] hover:shadow-sm",
        t.base,
        active ? `ring-2 ${t.ring} shadow-sm` : "",
      )}
    >
      <div className="text-[11px] uppercase tracking-wider text-slate-600 font-semibold">{label}</div>
      <div className="mt-1 text-2xl font-semibold tabular-nums text-slate-900">{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-0.5 tabular-nums">{sub}</div>}
    </button>
  );
}

// ---------- Type-to-search job picker ----------
function JobPicker({ value, onChange, jobs, clientById }) {
  const [open, setOpen] = useState(false);
  const selected = jobs.find((j) => j.id === value);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="charge-job-picker"
          className="mt-1 w-full h-9 rounded-sm border border-slate-300 bg-white text-left px-3 text-sm flex items-center justify-between hover:bg-slate-50"
        >
          <span className={selected ? "text-slate-900" : "text-slate-400"}>
            {selected
              ? `${selected.number} · ${(selected.date || "").slice(0, 10)} · ${clientById[selected.client_id]?.name || ""}`
              : "Pick a job or leave blank"}
          </span>
          <ChevronsUpDown className="w-3.5 h-3.5 text-slate-400 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[420px]" align="start">
        <Command>
          <CommandInput placeholder="Search by job number, client, date…" />
          <CommandList>
            <CommandEmpty>No jobs found.</CommandEmpty>
            <CommandGroup>
              <CommandItem
                value="__none"
                onSelect={() => { onChange(""); setOpen(false); }}
              >
                <span className="text-slate-500 italic">— No job link —</span>
              </CommandItem>
              {jobs.slice(0, 200).map((j) => {
                const label = `${j.number} · ${(j.date || "").slice(0, 10)} · ${clientById[j.client_id]?.name || "—"}`;
                return (
                  <CommandItem
                    key={j.id}
                    value={label + " " + j.id}
                    onSelect={() => { onChange(j.id); setOpen(false); }}
                  >
                    <Check className={cn("w-3.5 h-3.5 mr-2", value === j.id ? "opacity-100" : "opacity-0")} />
                    <span className="text-sm">{label}</span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
